import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import '../styles/SavedConfigs.css';

function SavedConfigs() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'top'

  // States
  const [myConfigs, setMyConfigs] = useState([]);
  const [topConfigs, setTopConfigs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Get user from WebApp
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      const userData = tg.initDataUnsafe?.user;
      if (userData) {
        setUser(userData);
      }
    }

    loadCountries();
    loadMyConfigs();
    loadTopConfigs();
  }, []);

  useEffect(() => {
    if (activeTab === 'top') {
      loadTopConfigs(selectedCountry);
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const response = await api.get('/countries/');
      setCountries(response.data || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadMyConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/saved-configs/');
      setMyConfigs(response.data.configurations || []);
    } catch (error) {
      console.error('Failed to load my configs:', error);
      setMyConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTopConfigs = async (countryCode = '') => {
    try {
      setLoading(true);
      const params = {};
      if (countryCode) {
        params.country_code = countryCode;
      }
      const response = await api.get('/config-ratings/top', { params });
      setTopConfigs(response.data || []);
    } catch (error) {
      console.error('Failed to load top configs:', error);
      setTopConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const rateConfiguration = async (configId, works) => {
    try {
      await api.post('/config-ratings/rate', {
        saved_config_id: configId,
        works: works
      });

      // Reload top configs to update ratings
      loadTopConfigs(selectedCountry);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: works ? '✅ Спасибо!' : '❌ Принято',
          message: 'Ваша оценка учтена',
          buttons: [{ type: 'ok' }]
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message;
      alert(`Ошибка: ${errorMessage}`);
    }
  };

  const deleteConfig = async (configId) => {
    if (!window.confirm('Удалить эту конфигурацию?')) {
      return;
    }

    try {
      await api.delete(`/saved-configs/${configId}`);
      loadMyConfigs();
    } catch (error) {
      alert('Ошибка удаления конфигурации');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('📋 Скопировано!');
    }).catch(() => {
      alert('Ошибка копирования');
    });
  };

  const openInHapp = (config) => {
    // Базовый URL подписки
    let subscriptionUrl = typeof config === 'string' ? config : config.subscription_url;

    // Получаем параметры конфигурации
    const params = typeof config === 'object' ? config.config_params : {};

    // Формируем параметры фрагментации для Happ
    const fragmentParams = [];

    if (params.fragment === 1 || params.fragment === '1') {
      // Формат Happ: fragment=length,interval,packets
      const length = params.length || '50-100';
      const interval = params.interval || '10-20';
      const packets = params.packets || 'tlshello';

      fragmentParams.push(`fragment=${length},${interval},${packets}`);
    }

    // Добавляем параметры к URL подписки если они есть
    let finalUrl = subscriptionUrl;
    if (fragmentParams.length > 0) {
      const separator = subscriptionUrl.includes('?') ? '&' : '?';
      finalUrl = `${subscriptionUrl}${separator}${fragmentParams.join('&')}`;
    }

    // Формируем Happ deep link согласно документации
    const configName = typeof config === 'object' ? config.name : 'VPN';
    const happUrl = `happ://add/${finalUrl}#${encodeURIComponent(configName)}`;

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(happUrl);
    } else {
      window.open(happUrl, '_blank');
    }
  };

  return (
    <div className="saved-configs-page">
      {/* Compact Header */}
      <div className="configs-header-compact">
        <h1>💾 Готовые конфигурации</h1>
        <p>Сохраненные и проверенные конфигурации</p>
      </div>

      {/* Tabs */}
      <div className="tabs-compact">
        <button
          className={`tab-compact ${activeTab === 'my' ? 'active' : ''}`}
          onClick={() => setActiveTab('my')}
        >
          📁 Мои
        </button>
        <button
          className={`tab-compact ${activeTab === 'top' ? 'active' : ''}`}
          onClick={() => setActiveTab('top')}
        >
          ⭐ Топ конфигураций
        </button>
      </div>

      {/* My Configs Tab */}
      {activeTab === 'my' && (
        <div className="panel-compact">
          <div className="section-header">
            <h3>Мои сохраненные</h3>
            <button
              className="help-button"
              onClick={() => setShowInstructions(!showInstructions)}
              title="Показать инструкцию"
            >
              ❓
            </button>
          </div>

          {/* Instructions */}
          {showInstructions && (
            <div className="instruction-panel">
              <h4>💡 Как работать с конфигурациями</h4>
              <ul>
                <li><strong>Сохранить</strong> - после успешного теста нажмите "Сохранить"</li>
                <li><strong>Открыть в Happ</strong> - запуск в защищенном браузере</li>
                <li><strong>Скопировать</strong> - для использования в других приложениях</li>
                <li><strong>Удалить</strong> - удалить конфигурацию из сохраненных</li>
              </ul>
            </div>
          )}

          {loading ? (
            <div className="loading-text">Загрузка...</div>
          ) : myConfigs.length === 0 ? (
            <div className="empty-state-compact">
              <div className="empty-icon">📭</div>
              <p>У вас пока нет сохраненных конфигураций</p>
              <button
                className="btn-primary-compact"
                onClick={() => navigate('/testing')}
              >
                Перейти к тестированию
              </button>
            </div>
          ) : (
            <div className="configs-list">
              {myConfigs.map((config) => (
                <div key={config.id} className="config-card-compact">
                  <div className="config-header">
                    <div className="config-name">
                      {config.country && countries.find(c => c.code === config.country)?.flag_emoji} {config.name}
                    </div>
                    <button
                      className="btn-delete"
                      onClick={() => deleteConfig(config.id)}
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>

                  <div className="config-params-display">
                    {config.config_params?.fragment && (
                      <span className="param-badge">fragment: {config.config_params.fragment}</span>
                    )}
                    {config.config_params?.packets && (
                      <span className="param-badge">packets: {config.config_params.packets}</span>
                    )}
                    {config.config_params?.provider && (
                      <span className="param-badge">{config.config_params.provider}</span>
                    )}
                  </div>

                  <div className="config-actions">
                    <button
                      className="btn-action"
                      onClick={() => openInHapp(config)}
                    >
                      🚀 Открыть в Happ
                    </button>
                    <button
                      className="btn-action"
                      onClick={() => copyToClipboard(config.subscription_url)}
                    >
                      📋 Копировать
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top Configs Tab */}
      {activeTab === 'top' && (
        <div className="panel-compact">
          <h3>⭐ Топ конфигураций</h3>

          {/* Country Filter */}
          <div className="form-group-compact">
            <label>🌐 Выбор страны:</label>
            <div className="country-selector">
              <button
                className={`country-option ${selectedCountry === '' ? 'selected' : ''}`}
                onClick={() => setSelectedCountry('')}
              >
                🌍 Все страны
              </button>
              {countries.map(country => (
                <button
                  key={country.code}
                  className={`country-option ${selectedCountry === country.code ? 'selected' : ''}`}
                  onClick={() => setSelectedCountry(country.code)}
                >
                  {country.flag_emoji} {country.name_ru}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="loading-text">Загрузка...</div>
          ) : topConfigs.length === 0 ? (
            <div className="empty-state-compact">
              <div className="empty-icon">📊</div>
              <p>Пока нет конфигураций с оценками</p>
            </div>
          ) : (
            <div className="configs-list">
              {topConfigs.map((config) => (
                <div key={config.id} className="config-card-compact top-config">
                  <div className="config-header">
                    <div className="config-name">
                      {config.country_flag} {config.name}
                    </div>
                    <div className="config-rating">
                      <span className="success-rate">{config.success_rate}%</span>
                      <span className="rating-count">({config.total_ratings})</span>
                    </div>
                  </div>

                  <div className="config-stats">
                    <div className="stat-item">
                      <span className="stat-icon">👍</span>
                      <span className="stat-value">{config.positive_ratings}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon">👎</span>
                      <span className="stat-value">{config.negative_ratings}</span>
                    </div>
                  </div>

                  <div className="config-actions">
                    <button
                      className="btn-action"
                      onClick={() => openInHapp(config)}
                    >
                      🚀 Открыть в Happ
                    </button>
                    <button
                      className="btn-action"
                      onClick={() => copyToClipboard(config.subscription_url)}
                    >
                      📋 Копировать
                    </button>
                  </div>

                  {/* Rating Buttons - только если пользователь еще не оценивал */}
                  {config.user_rating === null && (
                    <div className="rating-buttons">
                      <button
                        className="btn-rate success"
                        onClick={() => rateConfiguration(config.id, true)}
                      >
                        👍 Работает
                      </button>
                      <button
                        className="btn-rate danger"
                        onClick={() => rateConfiguration(config.id, false)}
                      >
                        👎 Не работает
                      </button>
                    </div>
                  )}

                  {/* User already rated */}
                  {config.user_rating !== null && (
                    <div className="user-rated">
                      {config.user_rating ? '✅ Вы отметили: работает' : '❌ Вы отметили: не работает'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SavedConfigs;
